import type { TableReact } from '@qlashgeminiz/qgammon-board-ui';

/**
 * Il tavolo in tempo reale del backgammon (I-217, ADR-0172): le emoji fra i due giocatori e la mossa
 * dell'avversario mentre la compone. Qui la parte pura, senza React.
 *
 * 🔴 L'emoji viaggia come CODICE (`TABLE_EMOJI` nei types ≥ 0.13.0), mai come testo: e' il server a
 * rifiutare tutto cio' che non sta nell'insieme fisso (Luca, L-2305: «insieme fisso di ~12 emoji
 * scelte da noi»), quindi non c'e' moderazione da fare. Il disegno lo decide QUESTA mappa: un codice
 * che non conosce (un server piu' nuovo) non si disegna — `glifoEmoji` torna `null`.
 */
export const EMOJI_TAVOLO = [
  ['thumbs_up', '👍'],
  ['clap', '👏'],
  ['laugh', '😂'],
  ['wow', '😮'],
  ['scream', '😱'],
  ['angry', '😡'],
  ['sad', '😢'],
  ['thinking', '🤔'],
  ['fire', '🔥'],
  ['luck', '🍀'],
  ['dice', '🎲'],
  ['pray', '🙏'],
] as const;

export type CodiceEmoji = (typeof EMOJI_TAVOLO)[number][0];

const GLIFI: ReadonlyMap<string, string> = new Map(EMOJI_TAVOLO);

/** Il glifo di un codice, o `null` per un codice che questa build non conosce. */
export function glifoEmoji(codice: string): string | null {
  return GLIFI.get(codice) ?? null;
}

/**
 * Il nome dell'evento sul socket. Scritto qui come letterale. Prima non si poteva prendere da
 * `ServerEvents`: la TMA dipendeva DIRETTAMENTE da `@qlashgeminiz/qgammon-types` 0.2.0, che l'evento non
 * ce l'ha (lo porta la 0.13.0). Dal 29/09 la dipendenza diretta e' 0.16.0 e `ServerEvents.TableEvent`
 * esiste ed e' `'table:event'`, quindi la sostituzione ORA e' possibile.
 * Il letterale resta di proposito: il test M2 (`tavolo-live-backgammon.test.tsx`) confronta QUESTO
 * valore con quello del pacchetto, e leggerlo dal pacchetto renderebbe quel confronto autoreferenziale.
 * Se si sostituisce, M2 va riscritto contro un letterale del filo, non contro questo export.
 */
export const EVENTO_TAVOLO = 'table:event' as const;

/** Un'emoji da mostrare in un fumetto: `seq` distingue due emoji uguali di fila. */
export interface Fumetto {
  glifo: string;
  seq: number;
}

/** I due lati del tavolo. Un fumetto per lato: la risposta del nero non cancella il bianco. */
export type Lato = 'W' | 'B';
export type FumettiPerLato = Readonly<Record<Lato, Fumetto | null>>;
export const NESSUN_FUMETTO: FumettiPerLato = { W: null, B: null };

/** Quanto resta a schermo un'emoji: abbastanza per leggerla, poco per coprire il tavolo. */
export const DURATA_FUMETTO_MS = 2500;

/**
 * L'emoji ricevuta, col lato su cui va mostrata, o `null`.
 *
 * ⛔ Mai quella marcata col MIO posto: il server non la rimanda al socket che l'ha mandata, ma
 * un'altra scheda dello stesso giocatore (il telefono e il web) la sente — mostrata qui, la mia
 * emoji tornerebbe come se l'avesse mandata l'avversario. ⚠️ E `null` qui vuol dire «ignorala», non
 * «spegni il fumetto»: chi la riceve non deve azzerare niente (revisione di #457, punto 4). Lo
 * spettatore (`mioPosto` null) non ha un posto: vede quelle di tutti e due, ciascuna sul suo lato.
 */
export function fumettoRicevuto(
  react: TableReact | null,
  mioPosto: Lato | null,
): { lato: Lato; fumetto: Fumetto } | null {
  if (!react || react.from === mioPosto) return null;
  const glifo = glifoEmoji(react.emoji);
  return glifo ? { lato: react.from, fumetto: { glifo, seq: react.seq } } : null;
}

/**
 * IL BUDGET DELLE EMOJI, copiato da quello del server (QGammon `connguard.ts`, `reactCapacity: 3`,
 * `reactRefillPerSec: 1/3`; `throttle.ts`, secchio a ricarica CONTINUA): tre di fila, poi una ogni 3 s.
 * Oltre il budget il server le scarta IN SILENZIO (ADR-0172 §4), e chi le manda vedrebbe partire un
 * fumetto che l'altro non riceve mai. Qui lo stesso secchio spegne i bottoni PRIMA.
 *
 * ⚠️ La ricarica e' un po' PIU' LENTA di quella del server (`MARGINE_MS`): il server conta dall'arrivo,
 * il client dalla partenza, e due emoji che viaggiano con ritardi diversi possono arrivare piu' vicine
 * di come sono partite. Il margine sbaglia nel verso prudente: un bottone spento 300 ms di troppo.
 * ⚠️ Riduce le perdite, non le azzera [M, simulazione della verifica di #457, ⚪3]: con un ritardo di rete
 * irregolare oltre il margine (media 100 ms: la quarta di una raffica persa 268 volte su 10.000), dopo un
 * ricaricamento della app (il secchio del client riparte pieno, quello del server resta), con due schede
 * dello stesso utente (il secchio del server e' uno per utente). ⛔ Una pausa fissa (era 1,5 s) non basta:
 * dalla sesta emoji ne spariva una su due, sempre (revisione di #457, punto 3, misurato).
 */
export const BUDGET_EMOJI = { capacita: 3, ricaricaMs: 3000, margineMs: 300 } as const;

export interface Secchio {
  gettoni: number;
  /** L'istante (ms) a cui `gettoni` e' aggiornato. */
  t: number;
}

const MS_PER_GETTONE = BUDGET_EMOJI.ricaricaMs + BUDGET_EMOJI.margineMs;

export function secchioPieno(t: number): Secchio {
  return { gettoni: BUDGET_EMOJI.capacita, t };
}

/**
 * Il secchio all'istante `t`: ricaricato per il tempo passato, mai oltre la capacita'. Un orologio che
 * torna INDIETRO (NTP, ora cambiata a mano) non regala gettoni ma riallinea l'ancora, come il server
 * (`throttle.ts`): senza, il secchio restava fermo per tutto il salto (verifica di #457, ⚪4).
 */
export function ricarica(s: Secchio, t: number): Secchio {
  if (t < s.t) return { gettoni: s.gettoni, t };
  if (t === s.t) return s;
  return { gettoni: Math.min(BUDGET_EMOJI.capacita, s.gettoni + (t - s.t) / MS_PER_GETTONE), t };
}

/** Un gettone all'istante `t`, se c'e': il secchio dopo, o `null` se e' vuoto (l'emoji non parte). */
export function consuma(s: Secchio, t: number): Secchio | null {
  const r = ricarica(s, t);
  return r.gettoni >= 1 ? { gettoni: r.gettoni - 1, t } : null;
}

/** Fra quanti ms c'e' di nuovo un gettone intero (0 se c'e' gia'). */
export function msAlProssimo(s: Secchio, t: number): number {
  const r = ricarica(s, t);
  return r.gettoni >= 1 ? 0 : Math.ceil((1 - r.gettoni) * MS_PER_GETTONE);
}
